import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit'
import axios from 'axios'

import { BASE_URL } from '@/api/api.constants'
import { getRegions } from '@/api/region/getRegions'
import { Branch, Country, Region } from '@/types'
import type { CountryFilterType } from './filterSlice'
import { RootState } from './store'

export interface RegionState {
  regions: Region[]
  isLoading: boolean
  error: string | null

  // PnL-specific region cache (country-based, NOT stored in sessionStorage)
  pnlRegions: Region[]
  pnlRegionsCountry: CountryFilterType // tracks which country these regions belong to
  pnlRegionsLoading: boolean
}

const getInitialState = (): RegionState => {
  try {
    const storedRegions = sessionStorage.getItem('regions')
    if (storedRegions) {
      const parsedRegions = JSON.parse(storedRegions) as Region[]
      if (parsedRegions && parsedRegions.length > 0) {
        return {
          regions: parsedRegions,
          isLoading: false,
          error: null,
          pnlRegions: [],
          pnlRegionsCountry: undefined,
          pnlRegionsLoading: false,
        }
      }
    }
  } catch (error) {
    console.warn('Failed to parse stored regions:', error)
  }

  return {
    regions: [],
    isLoading: false,
    error: null,
    pnlRegions: [],
    pnlRegionsCountry: undefined,
    pnlRegionsLoading: false,
  }
}

export const refreshRegions = createAsyncThunk<
  Region[],
  void,
  { state: RootState; rejectValue: string }
>('region/refreshRegions', async (_, { getState, rejectWithValue }) => {
  const state = getState()

  if (!state.auth.isAuthenticated) {
    return rejectWithValue('User not authenticated')
  }

  try {
    const response = await axios.get(`${BASE_URL}/regions/`)

    if (!response.data) {
      throw new Error('Failed to fetch regions')
    }

    sessionStorage.setItem('regions', JSON.stringify(response.data))

    return response.data
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to refresh regions'
    return rejectWithValue(errorMessage)
  }
})

/**
 * Fetch PnL-specific regions for a given country.
 * Maps country to country (US->US, UK->UK) and fetches regions.
 * Stored only in Redux (NOT in sessionStorage) to avoid overwriting global regions.
 */
export const fetchPnlRegions = createAsyncThunk<
  { regions: Region[]; country: CountryFilterType },
  CountryFilterType,
  { state: RootState; rejectValue: string }
>('region/fetchPnlRegions', async (country, { getState, rejectWithValue }) => {
  const state = getState()

  if (!state.auth.isAuthenticated) {
    return rejectWithValue('User not authenticated')
  }

  // Skip fetch if we already have regions for this country
  if (
    state.region.pnlRegionsCountry === country &&
    state.region.pnlRegions.length > 0
  ) {
    return { regions: state.region.pnlRegions, country }
  }

  try {
    const regions = await getRegions(country as Country)
    return { regions, country }
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to fetch PnL regions'
    return rejectWithValue(errorMessage)
  }
})

const regionSlice = createSlice({
  name: 'region',
  initialState: getInitialState(),
  reducers: {
    setRegions: (state, action: PayloadAction<Region[]>) => {
      state.regions = action.payload
      state.isLoading = false
      state.error = null

      sessionStorage.setItem('regions', JSON.stringify(action.payload))
    },

    clearRegions: (state) => {
      state.regions = []
      state.isLoading = false
      state.error = null
      sessionStorage.removeItem('regions')
    },

    clearRegionError: (state) => {
      state.error = null
    },

    /**
     * Clear PnL-specific regions cache.
     * Called on filter reset or when country reverts to default (USD).
     */
    clearPnlRegions: (state) => {
      state.pnlRegions = []
      state.pnlRegionsCountry = undefined
      state.pnlRegionsLoading = false
    },
  },
  extraReducers: (builder) => {
    builder
      // Global region refresh
      .addCase(refreshRegions.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(refreshRegions.fulfilled, (state, action) => {
        state.regions = action.payload
        state.isLoading = false
        state.error = null
      })
      .addCase(refreshRegions.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload || 'Failed to refresh regions'
      })
      // PnL-specific region fetch
      .addCase(fetchPnlRegions.pending, (state) => {
        state.pnlRegionsLoading = true
      })
      .addCase(fetchPnlRegions.fulfilled, (state, action) => {
        state.pnlRegions = action.payload.regions
        state.pnlRegionsCountry = action.payload.country
        state.pnlRegionsLoading = false
      })
      .addCase(fetchPnlRegions.rejected, (state) => {
        state.pnlRegionsLoading = false
      })
  },
})

export const { setRegions, clearRegions, clearRegionError, clearPnlRegions } =
  regionSlice.actions

export const regionReducer = regionSlice.reducer

// ============================================================================
// SELECTORS
// ============================================================================

export const selectRegions = (state: RootState) => state.region.regions
export const selectRegionsLoading = (state: RootState) => state.region.isLoading
export const selectRegionsError = (state: RootState) => state.region.error

export const selectAllBranches = createSelector(
  [selectRegions],
  (regions): string[] => {
    return regions.reduce((acc: string[], region: Region) => {
      return [...acc, ...region.branches.map((branch: Branch) => branch.name)]
    }, [])
  }
)

// PnL-specific region selectors
export const selectPnlRegions = (state: RootState) => state.region.pnlRegions
export const selectPnlRegionsLoading = (state: RootState) =>
  state.region.pnlRegionsLoading
export const selectPnlRegionsCountry = (state: RootState) =>
  state.region.pnlRegionsCountry

/** Comma-separated branch names from PnL regions (excludes "Other" region). Used to set default pnlBranch when UK loads. */
export const selectPnlAllBranchNames = createSelector(
  [selectPnlRegions],
  (regions): string => {
    return regions
      .filter((r) => r.name !== 'Other')
      .flatMap((r) => r.branches.map((b: Branch) => b.name))
      .join(',')
  }
)
